import * as React from 'react';
import { useEffect, useState } from 'react';

import { use$ } from '@legendapp/state/react';
import { Breakpoints, Text, TextTag, TokenTextAppearance } from '@volkswagen-onehub/components-core';
import styled, { css, keyframes } from 'styled-components';

import translate from '../../../utils/translate';

import { containerStore, dragOverlayStore, dragOverlayStoreActions, interiorStore } from '../../../store';
import { Turntable } from './turntable';

const APPEAR_DELAY = 2000;
const VISIBLE_DURATION = 3000;
const FADE_OUT_DURATION = 900;
const INTERSECTION_THRESHOLD = 0.2;

export const RotationOnboarding = () => {
	const isDragOverlayVisible = use$(dragOverlayStore.isDragOverlayVisible);
	const container = use$(containerStore.container) as HTMLElement | null;

	const [isVisible, setIsVisible] = useState(false);
	const [isFadingOut, setIsFadingOut] = useState(false);

	useEffect(() => {
		if (!container || !isDragOverlayVisible) return;

		let appearTimer: ReturnType<typeof setTimeout>;
		let visibleTimer: ReturnType<typeof setTimeout>;
		let fadeTimer: ReturnType<typeof setTimeout>;

		const observer = new IntersectionObserver(
			entries => {
				const entry = entries[0];
				if (!entry?.isIntersecting) return;

				observer.disconnect();

				appearTimer = setTimeout(() => {
					if (!dragOverlayStore.isDragOverlayVisible.get() || interiorStore.isInteriorLoaded.get()) return;

					setIsVisible(true);

					visibleTimer = setTimeout(() => {
						setIsFadingOut(true);
						fadeTimer = setTimeout(() => {
							dragOverlayStoreActions.setIsDragOverlayVisible(false);
						}, FADE_OUT_DURATION);
					}, VISIBLE_DURATION);
				}, APPEAR_DELAY);
			},
			{ threshold: INTERSECTION_THRESHOLD }
		);

		observer.observe(container);

		return () => {
			observer.disconnect();
			clearTimeout(appearTimer);
			clearTimeout(visibleTimer);
			clearTimeout(fadeTimer);
		};
	}, [container, isDragOverlayVisible]);

	useEffect(() => {
		if (!isVisible) return;

		const dismiss = () => dragOverlayStoreActions.setIsDragOverlayVisible(false);

		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') dismiss();
		};

		window.addEventListener('keydown', handleKeyDown);
		container?.addEventListener('focusin', dismiss);
		container?.addEventListener('click', dismiss);

		return () => {
			window.removeEventListener('keydown', handleKeyDown);
			container?.removeEventListener('focusin', dismiss);
			container?.removeEventListener('click', dismiss);
		};
	}, [isVisible, container]);

	if (!isDragOverlayVisible || !isVisible) return null;

	return (
		<Overlay isFadingOut={isFadingOut} aria-hidden>
			<Turntable />
			<Label appearance={TokenTextAppearance.copy200} tag={TextTag.span}>
				{translate('onboarding.dragToRotate')}
			</Label>
		</Overlay>
	);
};

const overlayFadeIn = keyframes`
	from {
		opacity: 0;
	}
	to {
		opacity: 1;
	}
`;

const overlayFadeOut = keyframes`
	from {
		opacity: 1;
	}
	to {
		opacity: 0;
	}
`;

const labelSlideUp = keyframes`
	from {
		transform: translate(-50%, 12px);
	}
	to {
		transform: translate(-50%, 0);
	}
`;

const Overlay = styled.div<{ isFadingOut: boolean }>`
	position: absolute;
	inset: 0;
	z-index: 7;

	pointer-events: none;
	user-select: none;

	background: radial-gradient(50% 50% at 50% 50%, rgba(0, 0, 0, 0.64) 0%, rgba(0, 0, 0, 0.8) 100%);
	backdrop-filter: blur(4px);

	${({ isFadingOut }) =>
		isFadingOut
			? css`
					animation: ${overlayFadeOut} ${FADE_OUT_DURATION}ms ease forwards;
				`
			: css`
					animation: ${overlayFadeIn} 0.9s ease both;
				`}
`;

const Label = styled(Text)`
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translateX(-50%);

	color: rgb(255, 255, 255) !important;
	white-space: nowrap;

	animation: ${labelSlideUp} 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.2s both;

	@media (max-width: ${Breakpoints.b560 - 1}px) {
		top: 47%;
	}
`;
